import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, Library, Loader2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  buildRecommendationsEmailHtml,
  COLLECTION_LABELS,
  contentIndexItemKey,
  MANAGE_TOKEN_PLACEHOLDER,
  previewRecommendationLink,
  type ContentIndexItem,
} from '../../lib/contentIndex';
import RecommendContentPickerModal from './RecommendContentPickerModal';

interface RecommendContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberId: string;
  subscriberEmail?: string | null;
  subscriberName?: string | null;
  onSuccess?: () => void;
}

type Step = 'compose' | 'preview';

/** Delay before send — cancel from the subscriber's scheduled emails if needed. */
const SCHEDULE_DELAY_MINUTES = 10;

function defaultIntro(name?: string | null): string {
  const first = (name ?? '').trim().split(/\s+/)[0] || '';
  const greeting = first ? `Hi ${first},` : 'Hi,';
  return `${greeting}\n\nI came across a few things that might be useful for you.`;
}

function scheduleName(items: ContentIndexItem[]): string {
  if (items.length === 0) return 'Content recommendation';
  if (items.length === 1) return `Content rec: ${items[0].title.trim()}`;
  return `Content rec: ${items.length} links`;
}

function mergePickedItems(current: ContentIndexItem[], picked: ContentIndexItem[]): ContentIndexItem[] {
  const existingUrls = new Set(current.map((it) => it.url.trim()));
  const newOnes = picked.filter((p) => !existingUrls.has(p.url.trim()));
  return [...current, ...newOnes];
}

function canProceedToPreview(items: ContentIndexItem[], subject: string): string | null {
  if (items.length === 0) return 'Pick at least one page from the site';
  if (!subject.trim()) return 'Subject is required';
  return null;
}

export default function RecommendContentModal({
  isOpen,
  onClose,
  subscriberId,
  subscriberEmail,
  subscriberName,
  onSuccess,
}: RecommendContentModalProps) {
  const [step, setStep] = useState<Step>('compose');
  const [items, setItems] = useState<ContentIndexItem[]>([]);
  const [subject, setSubject] = useState('');
  const [intro, setIntro] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('compose');
    setItems([]);
    setSubject('');
    setIntro(defaultIntro(subscriberName));
    setPickerOpen(false);
  }, [isOpen, subscriberName]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving && !pickerOpen) {
        if (step === 'preview') setStep('compose');
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saving, pickerOpen, step]);

  const handlePickFromSite = (picked: ContentIndexItem[]) => {
    setItems((prev) => mergePickedItems(prev, picked));
    if (!subject.trim()) {
      if (picked.length === 1) {
        setSubject(`Thought of you: ${picked[0].title}`);
      } else if (picked.length > 1) {
        setSubject('Thought you might like these');
      }
    }
  };

  const bodyHtml = useMemo(() => {
    if (items.length === 0) return '';
    return buildRecommendationsEmailHtml({
      intro,
      items,
      autoLogin: true,
    });
  }, [items, intro]);

  const previewHtml = useMemo(
    () => bodyHtml.split(MANAGE_TOKEN_PLACEHOLDER).join('SAMPLE-TOKEN'),
    [bodyHtml],
  );

  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((it) => contentIndexItemKey(it) !== key));
  };

  function goToPreview() {
    const err = canProceedToPreview(items, subject);
    if (err) {
      toast.error(err);
      return;
    }
    setStep('preview');
  }

  const handleSchedule = async () => {
    const err = canProceedToPreview(items, subject);
    if (err) {
      toast.error(err);
      setStep('compose');
      return;
    }
    const subjectTrim = subject.trim();
    const when = new Date();
    when.setMinutes(when.getMinutes() + SCHEDULE_DELAY_MINUTES);
    when.setSeconds(0, 0);

    setSaving(true);
    try {
      const { error } = await supabase.from('scheduled_emails').insert({
        subscriber_id: subscriberId,
        name: scheduleName(items),
        subject: subjectTrim,
        body_html: bodyHtml,
        scheduled_at: when.toISOString(),
        status: 'pending',
      });
      if (error) throw error;
      toast.success(`Queued — sends in ~${SCHEDULE_DELAY_MINUTES} minutes (cancel from scheduled emails if needed)`);
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not queue the email');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-2xl flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recommend-content-modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2
              id="recommend-content-modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
            >
              <BookOpen className="w-5 h-5 text-emerald-500" />
              Recommend content
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Step {step === 'compose' ? '1' : '2'} of 2 —{' '}
              {step === 'compose' ? 'Build email' : 'Preview & queue'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
          {subscriberEmail ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Sending to{' '}
              <span className="font-medium text-gray-700 dark:text-gray-200">{subscriberEmail}</span>
            </div>
          ) : null}

          {step === 'compose' ? (
            <>
              <div>
                <label
                  htmlFor="recommend-subject"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Subject
                </label>
                <input
                  id="recommend-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Thought you might like these"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label
                  htmlFor="recommend-intro"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Intro{' '}
                  <span className="text-gray-400 dark:text-gray-500 font-normal">
                    (blank line = new paragraph)
                  </span>
                </label>
                <textarea
                  id="recommend-intro"
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Selected ({items.length})
                    </span>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Links go to the content page with a one-click login token.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                  >
                    <Library className="w-3.5 h-3.5" />
                    {items.length === 0 ? 'Pick from site' : 'Add more'}
                  </button>
                </div>

                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No pages selected yet.
                  </div>
                ) : (
                  <ul className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                    {items.map((item) => {
                      const key = contentIndexItemKey(item);
                      return (
                        <li key={key} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {item.title}
                            </div>
                            <div className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                              {COLLECTION_LABELS[item.collection]}
                            </div>
                            <div className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500 truncate font-mono">
                              {previewRecommendationLink(item.url)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(key)}
                            className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            aria-label={`Remove ${item.title}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 space-y-2">
                <div>
                  <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Subject
                  </span>
                  <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">{subject.trim()}</p>
                </div>
                <div>
                  <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Links
                  </span>
                  <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">
                    {items.length} page{items.length === 1 ? '' : 's'} with auto-login tokens
                  </p>
                </div>
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email preview
                </span>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white p-4 overflow-auto max-h-[50vh]">
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  Token placeholder shown as SAMPLE-TOKEN — real token is injected at send time.
                </p>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Queued sends go out in ~{SCHEDULE_DELAY_MINUTES} minutes. Cancel from the subscriber&apos;s
                scheduled emails if you need to stop it.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          {step === 'preview' ? (
            <button
              type="button"
              onClick={() => setStep('compose')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}

          {step === 'compose' ? (
            <button
              type="button"
              onClick={goToPreview}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
            >
              Preview
            </button>
          ) : (
            <button
              onClick={() => void handleSchedule()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              {saving ? 'Queuing…' : 'Queue recommendation'}
            </button>
          )}
        </div>
      </div>

      <RecommendContentPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={handlePickFromSite}
      />
    </div>
  );
}
